import apiBase from './api-base';

const BASE = '/challenges';

/* =============================================================== */
/*  Types                                                           */
/* =============================================================== */

export type ChallengeUnit = 'books' | 'genres';
export type ChallengeIcon = 'trophy' | 'globe' | 'grid' | 'sun' | 'star' | 'book';
export type ChallengeTint = 'navy' | 'gold' | 'green' | 'purple';
export type ChallengeStatus = 'upcoming' | 'active' | 'completed';

export interface Challenge {
  id: number;
  name: string;
  slug: string;
  desc: string;
  target: number;
  unit: ChallengeUnit;
  start_date: string;
  end_date: string;
  reward: string;
  icon_slug: ChallengeIcon;
  tint_slug: ChallengeTint;
  is_primary: boolean;
  status: ChallengeStatus;
  progress: number;
  participants: number;
  days_left: number;
  is_enrolled: boolean;
  my_progress: number | null;
}

export interface ChallengeManageInput {
  name: string;
  slug: string;
  desc: string;
  target: number;
  unit: ChallengeUnit;
  start_date: string;
  end_date: string;
  reward: string;
  icon_slug: ChallengeIcon;
  tint_slug: ChallengeTint;
  is_primary: boolean;
}

export interface ChallengeManage extends ChallengeManageInput {
  id: number;
}

export interface FormLeaderboardEntry {
  rank: number;
  form_name: string;
  books: number;
  avg: number;
  member_count: number;
  change: 'up' | 'down' | 'same';
  delta: number;
}

export interface ReaderLeaderboardEntry {
  rank: number;
  display_name: string;
  year: string;
  books: number;
  badge: string | null;
  streak: number;
}

export interface Participant {
  id: number;
  name: string;
  user_type: string;
  enrolled_at: string;
}

export interface ChallengeWinner {
  id: number;
  challenge: number;
  term_label: string;
  winning_form: string;
  form_total_books: number;
  top_reader_name: string;
  top_reader_books: number;
  published_at: string;
}

export interface ChallengeWinnerInput {
  challenge: number;
  term_label: string;
  winning_form: string;
  form_total_books: number;
  top_reader_name: string;
  top_reader_books: number;
}

export interface ReadLog {
  id: number;
  book: number | null;
  title: string;
  author: string;
  read_date: string;
  notes: string;
  created_at: string;
}

export interface ReadLogInput {
  book?: number | null;
  title?: string;
  author?: string;
  read_date: string;
  notes?: string;
}

export interface Milestone {
  threshold: number;
  name: string;
  desc: string;
}

export interface JoinResponse {
  enrolled: true;
}

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/* =============================================================== */
/*  Public / Student endpoints                                      */
/* =============================================================== */

export const challengesApi = {
  list: (): Promise<Challenge[]> =>
    apiBase.get<PaginatedResponse<Challenge>>(`${BASE}/`).then((r) => r.data.results),

  get: (slug: string): Promise<Challenge> =>
    apiBase.get<Challenge>(`${BASE}/${slug}/`).then((r) => r.data),

  join: (slug: string): Promise<JoinResponse> =>
    apiBase.post<JoinResponse>(`${BASE}/${slug}/join/`).then((r) => r.data),

  leave: (slug: string): Promise<void> =>
    apiBase.post(`${BASE}/${slug}/leave/`).then(() => undefined),
};

export const challengesLeaderboardApi = {
  getForms: (slug: string): Promise<FormLeaderboardEntry[]> =>
    apiBase
      .get<FormLeaderboardEntry[]>(`${BASE}/${slug}/leaderboard/forms/`)
      .then((r) => r.data),

  getReaders: (slug: string): Promise<ReaderLeaderboardEntry[]> =>
    apiBase
      .get<ReaderLeaderboardEntry[]>(`${BASE}/${slug}/leaderboard/readers/`)
      .then((r) => r.data),
};

export const challengesMilestonesApi = {
  list: (): Promise<Milestone[]> =>
    apiBase.get<Milestone[]>(`${BASE}/milestones/`).then((r) => r.data),
};

export const challengesReadLogsApi = {
  list: (): Promise<PaginatedResponse<ReadLog>> =>
    apiBase.get<PaginatedResponse<ReadLog>>(`${BASE}/read-logs/`).then((r) => r.data),

  create: (data: ReadLogInput): Promise<ReadLog> =>
    apiBase.post<ReadLog>(`${BASE}/read-logs/`, data).then((r) => r.data),

  destroy: (id: number): Promise<void> =>
    apiBase.delete(`${BASE}/read-logs/${id}/`).then(() => undefined),
};

/* =============================================================== */
/*  Hall of Fame (read for all, write for librarians)               */
/* =============================================================== */

export const challengesHallOfFameApi = {
  list: (): Promise<PaginatedResponse<ChallengeWinner>> =>
    apiBase
      .get<PaginatedResponse<ChallengeWinner>>(`${BASE}/hall-of-fame/`)
      .then((r) => r.data),

  create: (data: ChallengeWinnerInput): Promise<ChallengeWinner> =>
    apiBase
      .post<ChallengeWinner>(`${BASE}/hall-of-fame/`, data)
      .then((r) => r.data),

  destroy: (id: number): Promise<void> =>
    apiBase.delete(`${BASE}/hall-of-fame/${id}/`).then(() => undefined),
};

/* =============================================================== */
/*  Librarian-only endpoints                                        */
/* =============================================================== */

export const challengesParticipantsApi = {
  list: (slug: string): Promise<Participant[]> =>
    apiBase.get<Participant[]>(`${BASE}/${slug}/participants/`).then((r) => r.data),
};

export const challengesManageApi = {
  list: (): Promise<PaginatedResponse<ChallengeManage>> =>
    apiBase
      .get<PaginatedResponse<ChallengeManage>>(`${BASE}/manage/`)
      .then((r) => r.data),

  get: (slug: string): Promise<ChallengeManage> =>
    apiBase.get<ChallengeManage>(`${BASE}/manage/${slug}/`).then((r) => r.data),

  create: (data: ChallengeManageInput): Promise<ChallengeManage> =>
    apiBase.post<ChallengeManage>(`${BASE}/manage/`, data).then((r) => r.data),

  update: (slug: string, data: ChallengeManageInput): Promise<ChallengeManage> =>
    apiBase.put<ChallengeManage>(`${BASE}/manage/${slug}/`, data).then((r) => r.data),

  partialUpdate: (
    slug: string,
    data: Partial<ChallengeManageInput>,
  ): Promise<ChallengeManage> =>
    apiBase.patch<ChallengeManage>(`${BASE}/manage/${slug}/`, data).then((r) => r.data),

  destroy: (slug: string): Promise<void> =>
    apiBase.delete(`${BASE}/manage/${slug}/`).then(() => undefined),
};
